import { type FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ListingStatus,
  ListingFulfillmentType,
  InvalidAmountError,
  parseUsdcToBaseUnits,
  formatUsdcBaseUnits,
  LISTING_CATEGORY_VALUES,
  LISTING_CATEGORY_LABELS,
  ListingCategory,
  AccountPlatform,
  ACCOUNT_PLATFORM_VALUES,
  ACCOUNT_PLATFORM_LABELS,
} from "@hybrid-escrow/shared";
import { apiClient, newIdempotencyKey } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useSellerListing } from "../hooks/useSellerListing";
import { ListingStatusBadge } from "../components/StatusBadge";
import { ErrorBanner, LoadingState, describeError } from "../components/States";
import { UploadPanel } from "../components/UploadPanel";
import { CoverImageManager } from "../components/CoverImageManager";
import { LicenseKeyManager } from "../components/LicenseKeyManager";
import { formatDateTime } from "../lib/format";

/** NFT_TRANSFER-only: a live "check now" against the seller's own on-file wallet — required to
 *  pass before the listing can publish (see services/listings.ts' publishListing and
 *  lib/nftVerification.ts). Unlike DomainVerificationPanel below, there's no propagation delay to
 *  wait out: a failed check usually just means the token isn't in the seller's wallet (yet). */
function NftVerificationPanel({
  listingId,
  contractAddress,
  tokenId,
  verifiedAt,
  onVerified,
}: {
  listingId: string;
  contractAddress: string;
  tokenId: string;
  verifiedAt: string | null;
  onVerified: () => void;
}) {
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  async function handleCheck() {
    setCheckError(null);
    setIsChecking(true);
    try {
      await apiClient.verifyNftOwnership(listingId);
      onVerified();
    } catch (err) {
      setCheckError(describeError(err));
    } finally {
      setIsChecking(false);
    }
  }

  return (
    <div className="card stack">
      <h2>Verify NFT ownership</h2>
      {verifiedAt ? (
        <p className="alert alert--success" role="status">
          ✓ Verified {formatDateTime(verifiedAt)}.
        </p>
      ) : (
        <>
          <p className="text-sm muted">
            Make sure this token is currently in your own wallet, then check — this reads the
            contract directly, so there's no propagation delay to wait out.
          </p>
          <div className="row row--between">
            <span className="muted text-sm">Contract</span>
            <span className="mono">{contractAddress}</span>
          </div>
          <div className="row row--between">
            <span className="muted text-sm">Token ID</span>
            <span className="mono">{tokenId}</span>
          </div>
          {checkError && (
            <p className="field-error-text" role="alert">
              {checkError}
            </p>
          )}
          <div className="row">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={isChecking}
              onClick={() => void handleCheck()}
            >
              {isChecking ? "Checking…" : "Check now"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** DOMAIN_TRANSFER-only: the DNS TXT ownership challenge, shown while editing a DRAFT listing —
 *  required to pass before the listing can publish (see services/listings.ts' publishListing and
 *  lib/domainVerification.ts). Its own component so the "checking…" state doesn't get tangled up
 *  with the rest of the form's submit state. */
function DomainVerificationPanel({
  listingId,
  domainName,
  verificationToken,
  verifiedAt,
  onVerified,
}: {
  listingId: string;
  domainName: string;
  verificationToken: string;
  verifiedAt: string | null;
  onVerified: () => void;
}) {
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const challengeHost = `_wickedescrow-challenge.${domainName}`;

  async function handleCheck() {
    setCheckError(null);
    setIsChecking(true);
    try {
      await apiClient.verifyDomainOwnership(listingId);
      onVerified();
    } catch (err) {
      setCheckError(describeError(err));
    } finally {
      setIsChecking(false);
    }
  }

  return (
    <div className="card stack">
      <h2>Verify domain ownership</h2>
      {verifiedAt ? (
        <p className="alert alert--success" role="status">
          ✓ Verified {formatDateTime(verifiedAt)}.
        </p>
      ) : (
        <>
          <p className="text-sm muted">
            Add a DNS TXT record proving you control this domain — required before publishing.
          </p>
          <div className="row row--between">
            <span className="muted text-sm">Host</span>
            <span className="mono">{challengeHost}</span>
          </div>
          <div className="row row--between">
            <span className="muted text-sm">Value</span>
            <span className="mono">{verificationToken}</span>
          </div>
          <p className="text-sm muted">
            DNS changes can take up to 24-48 hours to propagate — it's fine to check now and again
            later.
          </p>
          {checkError && (
            <p className="field-error-text" role="alert">
              {checkError}
            </p>
          )}
          <div className="row">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={isChecking}
              onClick={() => void handleCheck()}
            >
              {isChecking ? "Checking…" : "Check now"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function CreateEditListingPage() {
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: config } = usePublicConfig();
  const { listing, isLoading, isError, error, refetch } = useSellerListing(id);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [category, setCategory] = useState<ListingCategory>(ListingCategory.OTHER);
  const [fulfillmentType, setFulfillmentType] = useState<ListingFulfillmentType>(
    ListingFulfillmentType.FILE,
  );
  const [accountPlatform, setAccountPlatform] = useState<AccountPlatform>(AccountPlatform.YOUTUBE);
  const [accountHandle, setAccountHandle] = useState("");
  const [domainName, setDomainName] = useState("");
  const [nftContractAddress, setNftContractAddress] = useState("");
  const [nftTokenId, setNftTokenId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (listing) {
      setTitle(listing.title);
      setDescription(listing.description);
      setPriceInput(formatUsdcBaseUnits(BigInt(listing.priceBaseUnits)));
      setCategory(listing.category);
      setFulfillmentType(listing.fulfillmentType);
      if (listing.accountPlatform) setAccountPlatform(listing.accountPlatform);
      setAccountHandle(listing.accountHandle ?? "");
      setDomainName(listing.domainName ?? "");
      setNftContractAddress(listing.nftContractAddress ?? "");
      setNftTokenId(listing.nftTokenId ?? "");
    }
  }, [listing]);

  if (isEditMode && isLoading) return <LoadingState label="Loading listing…" />;
  if (isEditMode && isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (isEditMode && !listing && !isLoading) {
    return <ErrorBanner error={new Error("Listing not found, or it isn't yours.")} />;
  }

  const minPrice = config ? BigInt(config.minListingPriceBaseUnits) : null;
  const isDraftEditable =
    !listing || listing.status === ListingStatus.DRAFT || listing.status === ListingStatus.REJECTED;
  // A live listing's title/description are frozen (a buyer's decision should match what they saw)
  // but the price and category can still change — see services/listings.ts' updateListing. This
  // is also what lets watchlist price-drop alerts fire at all.
  const isPublishedPriceEditable = listing?.status === ListingStatus.PUBLISHED;
  const canEditPriceOrCategory = isDraftEditable || isPublishedPriceEditable;
  const canPublish = listing
    ? listing.fulfillmentType === ListingFulfillmentType.LICENSE_KEY
      ? listing.status === ListingStatus.DRAFT && (listing.availableLicenseKeyCount ?? 0) >= 1
      : listing.fulfillmentType === ListingFulfillmentType.ACCOUNT_TRANSFER
        ? listing.status === ListingStatus.DRAFT
        : listing.fulfillmentType === ListingFulfillmentType.DOMAIN_TRANSFER
          ? listing.status === ListingStatus.DRAFT && Boolean(listing.domainOwnershipVerifiedAt)
          : listing.fulfillmentType === ListingFulfillmentType.NFT_TRANSFER
            ? listing.status === ListingStatus.DRAFT && Boolean(listing.nftOwnershipVerifiedAt)
            : listing.status === ListingStatus.READY
    : false;
  const canArchive = listing && listing.status !== ListingStatus.ARCHIVED;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    let priceBaseUnits: bigint;
    try {
      priceBaseUnits = parseUsdcToBaseUnits(priceInput.trim());
    } catch (err) {
      setFormError(err instanceof InvalidAmountError ? err.message : "Enter a valid USDC price.");
      return;
    }
    if (minPrice !== null && priceBaseUnits < minPrice) {
      setFormError(`Price must be at least $${formatUsdcBaseUnits(minPrice)}.`);
      return;
    }
    if (isDraftEditable) {
      if (title.trim().length < 3) {
        setFormError("Title must be at least 3 characters.");
        return;
      }
      if (description.trim().length < 1) {
        setFormError("Description can't be empty.");
        return;
      }
    }

    const isDomainTransfer = fulfillmentType === ListingFulfillmentType.DOMAIN_TRANSFER;
    if (isDomainTransfer && !domainName.trim()) {
      setFormError("Enter the domain you're selling (e.g. example.com).");
      return;
    }

    const isNftTransfer = fulfillmentType === ListingFulfillmentType.NFT_TRANSFER;
    if (isNftTransfer && (!nftContractAddress.trim() || !nftTokenId.trim())) {
      setFormError("Enter the NFT's contract address and token id.");
      return;
    }
    if (isNftTransfer && !/^0x[a-fA-F0-9]{40}$/.test(nftContractAddress.trim())) {
      setFormError("Contract address must be a 0x-prefixed 40-hex-character address.");
      return;
    }
    if (isNftTransfer && !/^\d+$/.test(nftTokenId.trim())) {
      setFormError("Token id must be a whole number.");
      return;
    }

    // Steam/Epic/PSN/Xbox and similar platforms' ToS explicitly prohibits account sales (see
    // MARKETPLACE_EXPANSION_PLAN.md's Phase 2 deferral) — FILE/LICENSE_KEY fulfillment can carry
    // any credential-shaped content with no platform restriction, so blocking this combination
    // client-side (the API enforces it too) closes that backdoor before a seller even submits.
    if (
      category === ListingCategory.GAME_ACCOUNTS &&
      (fulfillmentType === ListingFulfillmentType.FILE ||
        fulfillmentType === ListingFulfillmentType.LICENSE_KEY)
    ) {
      setFormError(
        "Game accounts (Steam, Epic, PSN, Xbox, etc.) can't be listed with file or license-key delivery — those platforms' account sales aren't supported.",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const supportsAccountHandle =
        fulfillmentType === ListingFulfillmentType.ACCOUNT_TRANSFER &&
        (accountPlatform === AccountPlatform.YOUTUBE || accountPlatform === AccountPlatform.X_TWITTER);

      if (isEditMode && id && isPublishedPriceEditable) {
        // Title/description (and, for a domain/NFT listing, the domain/contract+token itself) are
        // omitted entirely (not just unchanged) — the backend rejects a published-listing update
        // that even attempts to touch them, frozen or not.
        await apiClient.updateListing(id, {
          priceBaseUnits: priceBaseUnits.toString(),
          category,
          accountHandle: supportsAccountHandle ? accountHandle.trim() || undefined : undefined,
        });
        await queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings });
      } else if (isEditMode && id) {
        await apiClient.updateListing(id, {
          title: title.trim(),
          description: description.trim(),
          priceBaseUnits: priceBaseUnits.toString(),
          category,
          accountHandle: supportsAccountHandle ? accountHandle.trim() || undefined : undefined,
          domainName: isDomainTransfer ? domainName.trim() : undefined,
          nftContractAddress: isNftTransfer ? nftContractAddress.trim() : undefined,
          nftTokenId: isNftTransfer ? nftTokenId.trim() : undefined,
        });
        await queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings });
      } else {
        const created = await apiClient.createListing(
          {
            title: title.trim(),
            description: description.trim(),
            priceBaseUnits: priceBaseUnits.toString(),
            category,
            fulfillmentType,
            accountPlatform:
              fulfillmentType === ListingFulfillmentType.ACCOUNT_TRANSFER
                ? accountPlatform
                : undefined,
            accountHandle: supportsAccountHandle ? accountHandle.trim() || undefined : undefined,
            domainName: isDomainTransfer ? domainName.trim() : undefined,
            nftContractAddress: isNftTransfer ? nftContractAddress.trim() : undefined,
            nftTokenId: isNftTransfer ? nftTokenId.trim() : undefined,
          },
          newIdempotencyKey(),
        );
        await queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings });
        navigate(`/sell/${created.id}/edit`, { replace: true });
      }
    } catch (err) {
      setFormError(describeError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePublish() {
    if (!id) return;
    setActionError(null);
    try {
      await apiClient.publishListing(id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings });
      // Without this the button just greys out (canPublish goes false) with no sign it worked.
      navigate(`/listings/${id}`);
    } catch (err) {
      setActionError(describeError(err));
    }
  }

  async function handleArchive() {
    if (!id) return;
    setActionError(null);
    try {
      await apiClient.archiveListing(id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings });
    } catch (err) {
      setActionError(describeError(err));
    }
  }

  return (
    <div className="stack" style={{ maxWidth: 640 }}>
      <div className="row row--between">
        <h1>{isEditMode ? "Edit listing" : "New listing"}</h1>
        {listing && <ListingStatusBadge status={listing.status} />}
      </div>

      <form className="card stack" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="title">Title</label>
          <input
            id="title"
            type="text"
            required
            minLength={3}
            maxLength={140}
            disabled={!isDraftEditable}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            required
            maxLength={10_000}
            disabled={!isDraftEditable}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <span className="hint">Plain text only — shown to buyers exactly as written.</span>
        </div>

        <div className="field">
          <label htmlFor="category">Category</label>
          <select
            id="category"
            disabled={!canEditPriceOrCategory}
            value={category}
            onChange={(e) => setCategory(e.target.value as ListingCategory)}
          >
            {LISTING_CATEGORY_VALUES.map((value) => (
              <option key={value} value={value}>
                {LISTING_CATEGORY_LABELS[value]}
              </option>
            ))}
          </select>
          <span className="hint">
            {category === ListingCategory.GAME_CURRENCY_ITEMS
              ? "For code-redeemable currency and items only (e.g. V-Bucks, Robux, PSN/Xbox wallet " +
                "codes) — delivered the same way as a license key. We don't support live in-game " +
                "trades or transfers between accounts."
              : category === ListingCategory.GAME_ACCOUNTS
                ? "We don't support selling Steam, Epic, PlayStation, or Xbox accounts — those " +
                  "platforms' terms explicitly prohibit account sales. This category only works " +
                  "with \"Account transfer\" delivery below, and only for the platforms listed " +
                  "there (Discord servers or \"Other\" gaming-adjacent accounts, not the " +
                  "platforms above)."
                : "Helps buyers find your listing when browsing or searching."}
          </span>
        </div>

        <div className="field">
          <label htmlFor="fulfillment-type">Delivery method</label>
          {isEditMode ? (
            <p>
              {fulfillmentType === ListingFulfillmentType.LICENSE_KEY
                ? "License key (a code delivered automatically once funded)"
                : fulfillmentType === ListingFulfillmentType.ACCOUNT_TRANSFER
                  ? `Account transfer (${ACCOUNT_PLATFORM_LABELS[accountPlatform]}) — a guided, platform-verified handoff once funded`
                  : fulfillmentType === ListingFulfillmentType.DOMAIN_TRANSFER
                    ? "Domain transfer — a verified handoff of the domain's transfer code once funded"
                    : fulfillmentType === ListingFulfillmentType.NFT_TRANSFER
                      ? "NFT transfer — you send the NFT directly to the buyer's wallet once funded"
                      : "File upload (scanned, downloaded once funded)"}
            </p>
          ) : (
            <select
              id="fulfillment-type"
              value={fulfillmentType}
              onChange={(e) => setFulfillmentType(e.target.value as ListingFulfillmentType)}
            >
              <option value={ListingFulfillmentType.FILE}>File upload</option>
              <option value={ListingFulfillmentType.LICENSE_KEY}>License key</option>
              <option value={ListingFulfillmentType.ACCOUNT_TRANSFER}>Account transfer</option>
              <option value={ListingFulfillmentType.DOMAIN_TRANSFER}>Domain name</option>
              <option value={ListingFulfillmentType.NFT_TRANSFER}>NFT</option>
            </select>
          )}
          <span className="hint">
            {isEditMode
              ? "Can't be changed after creation — create a new listing to switch."
              : fulfillmentType === ListingFulfillmentType.ACCOUNT_TRANSFER
                ? "For a real account (YouTube, X, TikTok, etc.) — no file, no license key. We guide both sides through a verified handoff once the buyer funds the order."
                : fulfillmentType === ListingFulfillmentType.DOMAIN_TRANSFER
                  ? "For a registered domain name. You'll need to prove you control it (a quick DNS check) before publishing."
                  : fulfillmentType === ListingFulfillmentType.NFT_TRANSFER
                    ? "For an NFT you currently hold. We never take custody — once funded, you send it directly to the buyer's own wallet, and either side can check on-chain that it arrived."
                    : "License key skips file scanning: paste codes and each buyer gets one automatically."}
          </span>
        </div>

        {!isEditMode && fulfillmentType === ListingFulfillmentType.ACCOUNT_TRANSFER && (
          <div className="field">
            <label htmlFor="account-platform">Platform</label>
            <select
              id="account-platform"
              value={accountPlatform}
              onChange={(e) => setAccountPlatform(e.target.value as AccountPlatform)}
            >
              {ACCOUNT_PLATFORM_VALUES.map((value) => (
                <option key={value} value={value}>
                  {ACCOUNT_PLATFORM_LABELS[value]}
                </option>
              ))}
            </select>
            <span className="hint">
              Determines the handoff steps you and the buyer see on the order page — YouTube,
              X, and TikTok each work differently.
            </span>
          </div>
        )}

        {fulfillmentType === ListingFulfillmentType.ACCOUNT_TRANSFER &&
          (accountPlatform === AccountPlatform.YOUTUBE ||
            accountPlatform === AccountPlatform.X_TWITTER) && (
            <div className="field">
              <label htmlFor="account-handle">
                Public {accountPlatform === AccountPlatform.YOUTUBE ? "channel handle" : "username"}{" "}
                (optional)
              </label>
              <input
                id="account-handle"
                type="text"
                maxLength={100}
                disabled={isEditMode && !canEditPriceOrCategory}
                value={accountHandle}
                onChange={(e) => setAccountHandle(e.target.value)}
                placeholder={accountPlatform === AccountPlatform.YOUTUBE ? "somechannel" : "somehandle"}
              />
              <span className="hint">
                {listing?.accountStatsFetchedAt
                  ? `Verified — ${listing.accountStatsFollowerCount?.toLocaleString() ?? 0} ${accountPlatform === AccountPlatform.YOUTUBE ? "subscribers" : "followers"} as of the last check.`
                  : "Without a leading “@” or full URL. We check this against the platform's own public stats and show buyers a verified badge — purely a trust signal, never required to publish."}
              </span>
            </div>
          )}

        {fulfillmentType === ListingFulfillmentType.DOMAIN_TRANSFER &&
          (!isEditMode || isDraftEditable) && (
            <div className="field">
              <label htmlFor="domain-name">Domain name</label>
              <input
                id="domain-name"
                type="text"
                required
                maxLength={253}
                value={domainName}
                onChange={(e) => setDomainName(e.target.value)}
                placeholder="example.com"
              />
              <span className="hint">
                Locked once published. You'll need to add a DNS TXT record to prove you control it
                before you can publish.
              </span>
            </div>
          )}

        {fulfillmentType === ListingFulfillmentType.NFT_TRANSFER &&
          (!isEditMode || isDraftEditable) && (
            <>
              <div className="field">
                <label htmlFor="nft-contract-address">Contract address</label>
                <input
                  id="nft-contract-address"
                  type="text"
                  required
                  maxLength={42}
                  value={nftContractAddress}
                  onChange={(e) => setNftContractAddress(e.target.value)}
                  placeholder="0x..."
                />
              </div>
              <div className="field">
                <label htmlFor="nft-token-id">Token ID</label>
                <input
                  id="nft-token-id"
                  type="text"
                  inputMode="numeric"
                  required
                  maxLength={78}
                  value={nftTokenId}
                  onChange={(e) => setNftTokenId(e.target.value)}
                  placeholder="1234"
                />
                <span className="hint">
                  Locked once published. You'll need to prove you currently hold this token (a
                  live on-chain check) before you can publish.
                </span>
              </div>
            </>
          )}

        <div className="field">
          <label htmlFor="price">Price (USDC)</label>
          <input
            id="price"
            type="text"
            inputMode="decimal"
            required
            disabled={!canEditPriceOrCategory}
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
          />
          <span className="hint">
            This is your order principal — buyers separately pay a 10% platform fee on top.
            {config && ` Minimum $${formatUsdcBaseUnits(BigInt(config.minListingPriceBaseUnits))}.`}
            {isPublishedPriceEditable &&
              " Lowering the price emails everyone watching this listing."}
          </span>
        </div>

        {formError && (
          <p className="field-error-text" role="alert">
            {formError}
          </p>
        )}

        {canEditPriceOrCategory && (
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting
              ? "Saving…"
              : isPublishedPriceEditable
                ? "Update price & category"
                : isEditMode
                  ? "Save changes"
                  : "Create draft"}
          </button>
        )}
        {isPublishedPriceEditable && (
          <p className="text-sm muted">
            Title and description are locked once a listing is published.
          </p>
        )}
        {!canEditPriceOrCategory && (
          <p className="text-sm muted">
            This listing can&apos;t be edited while it&apos;s {listing?.status.toLowerCase()}.
          </p>
        )}
      </form>

      {isEditMode && listing && canEditPriceOrCategory && <CoverImageManager listing={listing} />}

      {isEditMode && listing && listing.fulfillmentType === ListingFulfillmentType.LICENSE_KEY && (
        <LicenseKeyManager
          listingId={listing.id}
          availableCount={listing.availableLicenseKeyCount ?? 0}
        />
      )}

      {isEditMode && listing && listing.fulfillmentType === ListingFulfillmentType.FILE && (
        <div className="card stack">
          <h2>Product file</h2>
          {listing.status === ListingStatus.SCANNING && (
            <p className="alert alert--warning" role="status">
              Scanning your file for malware. This page updates automatically.
            </p>
          )}
          {listing.status === ListingStatus.REJECTED && (
            <p className="alert alert--danger" role="alert">
              Your last file was rejected (failed the security scan or policy check). Upload a new
              file to try again.
            </p>
          )}
          {listing.status === ListingStatus.READY && (
            <p className="alert alert--success" role="status">
              Your file passed scanning. You can publish this listing.
            </p>
          )}
          <UploadPanel
            listingId={listing.id}
            disabled={
              listing.status === ListingStatus.SCANNING ||
              listing.status === ListingStatus.PUBLISHED
            }
            onUploaded={() =>
              void queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings })
            }
          />
        </div>
      )}

      {isEditMode &&
        listing &&
        listing.fulfillmentType === ListingFulfillmentType.DOMAIN_TRANSFER &&
        listing.domainName &&
        listing.domainVerificationToken &&
        listing.status === ListingStatus.DRAFT && (
          <DomainVerificationPanel
            listingId={listing.id}
            domainName={listing.domainName}
            verificationToken={listing.domainVerificationToken}
            verifiedAt={listing.domainOwnershipVerifiedAt}
            onVerified={() => void queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings })}
          />
        )}

      {isEditMode &&
        listing &&
        listing.fulfillmentType === ListingFulfillmentType.NFT_TRANSFER &&
        listing.nftContractAddress &&
        listing.nftTokenId &&
        listing.status === ListingStatus.DRAFT && (
          <NftVerificationPanel
            listingId={listing.id}
            contractAddress={listing.nftContractAddress}
            tokenId={listing.nftTokenId}
            verifiedAt={listing.nftOwnershipVerifiedAt}
            onVerified={() => void queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings })}
          />
        )}

      {isEditMode && listing && (
        <div className="card stack">
          <h2>Publish</h2>
          {actionError && (
            <p className="field-error-text" role="alert">
              {actionError}
            </p>
          )}
          <div className="row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canPublish}
              onClick={() => void handlePublish()}
            >
              Publish listing
            </button>
            {canArchive && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void handleArchive()}
              >
                Archive
              </button>
            )}
          </div>
          {!canPublish && listing.status !== ListingStatus.PUBLISHED && (
            <p className="text-sm muted">
              {listing.fulfillmentType === ListingFulfillmentType.LICENSE_KEY
                ? "Publishing is disabled until you add at least one license key."
                : listing.fulfillmentType === ListingFulfillmentType.ACCOUNT_TRANSFER
                  ? listing.status === ListingStatus.ARCHIVED
                    ? "This account has already been sold and can't be re-published."
                    : "Publishing is disabled for this listing."
                  : listing.fulfillmentType === ListingFulfillmentType.DOMAIN_TRANSFER
                    ? listing.status === ListingStatus.ARCHIVED
                      ? "This domain has already been sold and can't be re-published."
                      : "Publishing is disabled until you verify domain ownership above."
                    : listing.fulfillmentType === ListingFulfillmentType.NFT_TRANSFER
                      ? listing.status === ListingStatus.ARCHIVED
                        ? "This NFT has already been sold and can't be re-published."
                        : "Publishing is disabled until you verify NFT ownership above."
                      : "Publishing is disabled until the uploaded file passes scanning."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
